import React from 'react';
import { detachPageSeo } from '@/utils/pageSeo';
import { getAbsoluteSiteUrl } from '@/data/blogSeoMetadata';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import type { LegalDocument } from './legalDocuments';
import styles from './LegalDocumentPage.module.css';

const extractBodyHtml = (html: string) => {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match?.[1] ?? html;
};

interface LegalDocumentPageProps {
  legalDocument: LegalDocument;
}

const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ legalDocument }) => {
  const navigate = useNavigate();
  const bodyHtml = React.useMemo(() => extractBodyHtml(legalDocument.html), [legalDocument.html]);

  const handleBack = React.useCallback(() => {
    const historyState = window.history.state as { idx?: number } | null;
    if (typeof historyState?.idx === 'number' && historyState.idx > 0) {
      navigate(-1);
      return;
    }

    navigate('/');
  }, [navigate]);

  React.useEffect(() => {
    const previousTitle = document.title;
    const restorePageSeo = detachPageSeo();
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = getAbsoluteSiteUrl(legalDocument.route);
    document.head.appendChild(canonical);
    window.document.title = `${legalDocument.title} | CalorieScience`;
    return () => {
      canonical.remove();
      restorePageSeo();
      document.title = previousTitle;
    };
  }, [legalDocument.title, legalDocument.route]);

  return (
    <div className={styles.page}>
      <Navbar />
      <main className={styles.main}>
        <div className={styles.shell}>
          <header className={styles.header}>
            <button type="button" onClick={handleBack} className={styles.backLink}>
              <ArrowLeft aria-hidden="true" size={18} />
              Back
            </button>
            <p className={styles.eyebrow}>{legalDocument.sourceDocument}</p>
            <h1 id="legal-document-title" className={styles.title}>{legalDocument.title}</h1>
            <p className={styles.meta}>{legalDocument.documentMeta}</p>
          </header>

          <article className={styles.documentFrame} aria-labelledby="legal-document-title">
            <div
              className={styles.documentContent}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalDocumentPage;
